import { useEffect, useState } from "react";
import { Heart } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const LS_KEY = "ks_favorites";

const _readLocal = () => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch {
    return new Set();
  }
};

const _writeLocal = (set) => {
  try { localStorage.setItem(LS_KEY, JSON.stringify(Array.from(set))); } catch {}
};

// One in-flight refresh per session, shared across every card
let _refreshPromise = null;
const _refreshFromApi = () => {
  if (!_refreshPromise) {
    _refreshPromise = api.get("/favorites")
      .then(({ data }) => {
        const ids = new Set(data.map((k) => k.id));
        _writeLocal(ids);
        return ids;
      })
      .catch(() => _readLocal());
  }
  return _refreshPromise;
};

export const invalidateFavoritesCache = () => { _refreshPromise = null; };

export function FavoriteButton({ kitchenId }) {
  const { user } = useAuth();
  // Hydrate synchronously from localStorage so hearts light up on first paint
  const [on, setOn] = useState(() => _readLocal().has(kitchenId));
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user?.role !== "cook") return;
    let alive = true;
    // Background refresh — reconciles any stale local state
    _refreshFromApi().then((ids) => { if (alive) setOn(ids.has(kitchenId)); });
    return () => { alive = false; };
  }, [kitchenId, user]);

  if (user?.role !== "cook") return null;

  const toggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    const next = !on;
    setOn(next);
    // Optimistic localStorage update
    const local = _readLocal();
    if (next) local.add(kitchenId); else local.delete(kitchenId);
    _writeLocal(local);
    try {
      if (next) {
        await api.post(`/favorites/${kitchenId}`);
        toast.success("Saved to favorites");
      } else {
        await api.delete(`/favorites/${kitchenId}`);
        toast.success("Removed from favorites");
      }
      invalidateFavoritesCache();
    } catch (err) {
      // Roll back both UI and localStorage
      setOn(!next);
      const rb = _readLocal();
      if (next) rb.delete(kitchenId); else rb.add(kitchenId);
      _writeLocal(rb);
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      data-testid={`favorite-btn-${kitchenId}`}
      onClick={toggle}
      aria-label={on ? "Remove from favorites" : "Save to favorites"}
      className={`absolute top-3 right-3 flex items-center justify-center w-9 h-9 border transition-colors ${on ? "bg-[#FF3B30] border-[#FF3B30] text-white" : "border-white/40 bg-black/50 text-white hover:border-[#FF3B30] hover:text-[#FF3B30]"}`}
    >
      <Heart size={16} strokeWidth={2.4} fill={on ? "currentColor" : "none"} />
    </button>
  );
}
